import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../api/client';

const AuthContext = createContext(null);

const ROLE_DEFAULTS = {
  admin: { 'project.create': true, 'project.edit': true, 'project.delete': true, 'project.view_budget': true, 'project.view_all': true, 'measurement.approve': true, 'payment.add': true, 'payment.view': true, 'user.manage': true, 'leave.approve': true, 'expense.create': true, 'expense.edit': true, 'issue.close': true, 'drawing.upload': true, 'chat.pin_message': true, 'incentive.configure': true, 'report.export': true, 'admin.panel': true, 'audit.view': true, 'photoshoot.manage': true },
  md: { 'project.create': true, 'project.edit': true, 'project.delete': true, 'project.view_budget': true, 'project.view_all': true, 'measurement.approve': true, 'payment.add': true, 'payment.view': true, 'user.manage': true, 'leave.approve': true, 'expense.create': true, 'expense.edit': true, 'issue.close': true, 'drawing.upload': true, 'chat.pin_message': true, 'incentive.configure': true, 'report.export': true, 'admin.panel': true, 'audit.view': true, 'photoshoot.manage': true },
  manager: { 'project.edit': true, 'leave.approve': true, 'issue.close': true, 'chat.pin_message': true, 'report.export': true },
  supervisor: { 'measurement.submit': true, 'expense.create': true },
  purchase: { 'project.create': true, 'project.edit': true, 'project.view_all': true },
  accounts: { 'payment.add': true, 'payment.view': true, 'project.view_budget': true, 'project.view_all': true, 'report.export': true },
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data.user);
    } catch {
      localStorage.removeItem('accessToken');
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    localStorage.setItem('accessToken', data.accessToken);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch {}
    localStorage.removeItem('accessToken');
    setUser(null);
  };

  const hasPermission = (key) => {
    if (!user) return false;
    const overrides = user.permissions || {};
    if (key in overrides) return overrides[key];
    const defaults = ROLE_DEFAULTS[user.role] || {};
    return defaults[key] || false;
  };

  const isAdmin = user && ['admin', 'md'].includes(user.role);
  const isManager = user && user.role === 'manager';
  const isSupervisor = user && user.role === 'supervisor';

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, hasPermission, isAdmin, isManager, isSupervisor, loadUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
